import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useModalStrip from '../hooks/global-hooks/useModalStrip';
import { Phone, Lock, Eye, EyeOff, LogIn, ShieldCheck, AlertCircle, Sparkles } from 'lucide-react';
import './LoginPage.css';

interface LoginState {
  phone: string;
  password: string;
  error: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { showModalStrip } = useModalStrip();
  const auth = useAuth();
  const [state, setState] = useState<LoginState>({
    phone: '',
    password: '',
    error: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
      error: '' // Clear error when typing
    }));
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.phone.trim() || !state.password.trim()) {
      setState((prevState) => ({
        ...prevState,
        error: 'Please enter both phone number and password.'
      }));
      return;
    }

    setState((prevState) => ({
      ...prevState,
      error: ''
    }));

    setLoading(true);
    auth.login(state.phone, state.password)
      .then(() => {
        navigate('/dashboard');
      })
      .catch((err: Error) => {
        const errorMsg = err.message || 'Login failed. Please check your credentials.';
        setState((prevState) => ({
          ...prevState,
          error: errorMsg
        }));
        showModalStrip('danger', errorMsg, 5000);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="login-page-container">
      {/* Dynamic ambient background glow circles */}
      <div className="ambient-glow glow-1" />
      <div className="ambient-glow glow-2" />
      <div className="ambient-glow glow-3" />

      <div className="login-card-wrapper">
        <div className="login-card">
          {/* Header & Brand */}
          <div className="login-brand-header">
            <div className="brand-logo-badge">
              <Sparkles size={28} className="badge-sparkle-icon" />
            </div>
            <h1 className="brand-title">Bizzboy</h1>
            <p className="brand-subtitle">Sign in to your dashboard</p>
          </div>

          {/* Error Message */}
          {state.error && (
            <div className="login-error-alert" role="alert">
              <AlertCircle size={18} className="error-icon" />
              <span>{state.error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleLogin} className="login-form">
            <div className="input-group-container">
              <label htmlFor="login-phone" className="input-label">
                Phone Number
              </label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <Phone size={18} />
                </span>
                <input
                  id="login-phone"
                  type="tel"
                  placeholder="Enter phone number"
                  name="phone"
                  value={state.phone}
                  onChange={handleChange}
                  required
                  autoComplete="tel"
                  className="modern-input"
                />
              </div>
            </div>

            <div className="input-group-container">
              <label htmlFor="login-password" className="input-label">
                Password
              </label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <Lock size={18} />
                </span>
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  name="password"
                  value={state.password}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                  className="modern-input password-input"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className={`modern-login-btn ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <div className="btn-spinner-content">
                  <span className="btn-spinner" />
                  <span>Signing In...</span>
                </div>
              ) : (
                <div className="btn-content">
                  <span>Sign In</span>
                  <LogIn size={18} className="btn-icon" />
                </div>
              )}
            </button>
          </form>

          {/* Footer Security Note */}
          <div className="login-footer">
            <ShieldCheck size={16} className="security-icon" />
            <span>End-to-end encrypted & secure</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
